import { useCallback, useEffect, useState } from "react";
import { isOfMetaOrigin } from "../utils/marketing-url-params";
import { flightsSessionStore, getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import { UIBrandedFare, UIClientFetchError, UIFlightData } from "@flights/types";
import { actions } from "../store/metaFlightDetails/actions";
// eslint-disable-next-line import/no-deprecated
import { useActions, useStore } from "../store";
import { useClientFetch } from "./useClientFetch";
import useGetUrlParams from "./useGetUrlParams";
import useTrackFlightsWebUseOfferPos from "./useTrackFlightsWebUseOfferPosExp";
import useGetSearchResultsBound from "./useGetSearchResultsBound";
import { getSearchResultsFlights } from "../store/searchResults/selectors";
import { getSearchResultsFlights as getOBSearchResultsFlights } from "../store/searchResultsBounds/outboundSelectors";
import { getSearchResultsFlights as getIBSearchResultsFlights } from "../store/searchResultsBounds/inboundSelectors";
import { useSelector } from "react-redux";
import { LANDING_OFFER_FROM_META_KEY } from "../constants";
import { trackWithDefaultStage } from "../utils/et-microconversion";
import { useHistory } from "react-router-dom";
import useCreateUrl from "./useCreateUrl";

export function isBrandedFareOffer(offerToken: string) {
  const [prefix] = offerToken.split("@");
  return prefix.includes("3904B");
}

const useGetBoundSearchResults = () => {
  const bound = useGetSearchResultsBound();
  const searchResults = useSelector(getSearchResultsFlights);
  const outboundSearchResults = useSelector(getOBSearchResultsFlights);
  const inboundSearchResults = useSelector(getIBSearchResultsFlights);

  if (bound === "outbound") {
    return outboundSearchResults;
  }

  if (bound === "inbound") {
    return inboundSearchResults;
  }

  return searchResults;
};

const useFlightDetailsFromSearchResults = (offerReference: string) => {
  const [flight, setFlight] = useState<UIFlightData | undefined>(undefined);

  // eslint-disable-next-line import/no-deprecated
  const store = useStore();
  const history = useHistory();
  const { createUrl } = useCreateUrl();

  const { flight: fullFlightDetails } = store.flightDetails;
  const { fareOffersWithDetails } = store.brandedFares;

  // eslint-disable-next-line import/no-deprecated
  const metaFlightDetailsActions = useActions(actions);
  const flightsWebUseOfferPos = useTrackFlightsWebUseOfferPos();
  const baseOfferToken = useGetUrlParams().getUrlParam("baseOfferToken") || "";
  const searchResultsFlights = useGetBoundSearchResults();
  const pinMetaOfferOnSrExperimentEnabled =
    !!trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_mdot", 1) ||
    !!trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_www", 1);

  const handlePinMetaOfferOnSr = useCallback(
    (uiFlightData: UIFlightData) => {
      const urlParams = new URLSearchParams(location.search);
      if (
        pinMetaOfferOnSrExperimentEnabled &&
        flightsSessionStore.get(LANDING_OFFER_FROM_META_KEY) === offerReference
      ) {
        flightsSessionStore.set("landingOfferKey", uiFlightData.offerKeyToHighlight);
        history.replace(createUrl(`${location.pathname}?${urlParams.toString()}`));
      }
    },
    [pinMetaOfferOnSrExperimentEnabled, history, createUrl, offerReference]
  );

  useEffect(() => {
    if (!flight) {
      setFlight(() => {
        if (isBrandedFareOffer(offerReference) && baseOfferToken && fareOffersWithDetails[baseOfferToken]) {
          const brandedFares = fareOffersWithDetails[baseOfferToken].brandedFareOffers ?? [];
          const fare = brandedFares && brandedFares.find((fare: UIBrandedFare) => fare.token == offerReference);
          if (fare) {
            const baseOffer = searchResultsFlights.find((flight) => flight.token == baseOfferToken);
            if (baseOffer) {
              return { ...baseOffer, ...fare };
            }
          }
        }

        return searchResultsFlights.find((flight) => flight.token == offerReference);
      });
    }
  }, [offerReference, store.brandedFares, flight, fareOffersWithDetails, baseOfferToken, searchResultsFlights]);

  const searchParams = new URLSearchParams();
  if (flightsWebUseOfferPos.isVariant) {
    const salesCountry = getMarketingTrackingVariable("salesCountry");
    salesCountry && searchParams.set("salesCountry", salesCountry);
  }

  const url = `/api/flightFromOfferRelatedFlights/${offerReference}?${searchParams.toString()}`;

  //For meta landings, fetch the flight details from cached search from supplier
  useClientFetch(url, {
    condition: () => {
      const flightFromSearch = store.searchResults.selectedInboundFlight
        ? store.searchResults.selectedInboundFlight
        : store.searchResults.flights.find((flight) => flight.token == offerReference);

      const condition = !flightFromSearch && !fullFlightDetails && isOfMetaOrigin();
      return Boolean(condition);
    },
    fetch: () => {
      metaFlightDetailsActions.fetch();
    },
    success: (uiFlightData: UIFlightData) => {
      if (!flight && uiFlightData && Object.keys(uiFlightData).length) {
        metaFlightDetailsActions.fetchSuccess(uiFlightData);

        handlePinMetaOfferOnSr(uiFlightData);

        setFlight(uiFlightData);
      }
    },
    error: (error: UIClientFetchError) => {
      metaFlightDetailsActions.fetchError(error);
    }
  });

  return flight;
};
export default useFlightDetailsFromSearchResults;
